// src/components/viewer/ExpiryRenewal.jsx
//
// Surfaces access expiry + re-baseline on the requester Viewer page.
// Guards (agreed):
//   • accessEndDate is shown ONLY when the request is APPROVED (access only
//     exists once granted; showing an end date on a pending request misleads).
//   • The "Re-baseline now" CTA appears ONLY when APPROVED + daysLeft <= 30 +
//     there is no existing renewalRequestId (matches the expiry-engine window).
//   • If a renewalRequestId exists, we show "Renewed → PAM-XXXX" instead of a CTA.
//   • Otherwise the component renders nothing (keeps the viewer uncluttered).
//
// Pure frontend: all fields come from get_results (no backend call).

import React from 'react';

const RENEWAL_WINDOW_DAYS = 30; // matches the expiry engine's 30-day window

// ceil((endDate at 00:00 UTC) − now UTC) in whole days; null if unparseable.
const daysUntil = (iso) => {
  if (!iso) return null;
  // Accept 'YYYY-MM-DD' (normalized) — construct at UTC midnight.
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const ExpiryRenewal = ({ data, requestId }) => {
  // Approved gate — use approvalStatus, fall back to status.
  const status = (data?.approvalStatus || data?.status || '').toUpperCase();
  const isApproved = status === 'APPROVED';
  if (!isApproved) return null; // guard 1: only for approved access

  const accessEndDate = data?.accessEndDate;
  const renewalRequestId = data?.renewalRequestId;
  const daysLeft = daysUntil(accessEndDate);

  // Nothing meaningful to show if there's no valid end date and no renewal.
  if (!accessEndDate && !renewalRequestId) return null;

  // Already re-baselined → point at the renewal, no CTA.
  const alreadyRenewed = !!renewalRequestId;
  // CTA only within the window, and only if not already renewed. (guard 2)
  const inWindow = daysLeft !== null && daysLeft >= 0 && daysLeft <= RENEWAL_WINDOW_DAYS;
  const showRebaselineCta = !alreadyRenewed && inWindow;
  const expired = daysLeft !== null && daysLeft < 0;

  const rebaselineLink =
    `/request/individual?type=renewal&originalRequestId=${encodeURIComponent(requestId)}`;
  const renewalViewerLink = renewalRequestId
    ? `/viewer?id=${encodeURIComponent(renewalRequestId)}`
    : null;

  // Accent color: red if expired/urgent window, amber if in window, neutral otherwise.
  const accent = expired ? '#b91c1c' : (inWindow ? '#92400e' : '#334155');
  const accentBg = expired ? '#fef2f2' : (inWindow ? '#fff7ed' : '#f8fafc');
  const accentBorder = expired ? '#fecaca' : (inWindow ? '#fed7aa' : '#e2e8f0');

  return (
    <div style={{
      background: accentBg,
      border: `1px solid ${accentBorder}`,
      borderRadius: '10px',
      padding: '16px 20px',
      marginTop: '12px',
    }}>
      <div style={{ fontSize: '15px', fontWeight: '800', color: '#111', marginBottom: '8px' }}>
        🗓️ Access Expiry
      </div>

      {accessEndDate && (
        <div style={{ fontSize: '14px', color: '#111', marginBottom: alreadyRenewed || showRebaselineCta ? '12px' : 0 }}>
          {expired ? (
            <>Access <strong>expired</strong> on <strong>{fmtDate(accessEndDate)}</strong>.</>
          ) : (
            <>
              Access ends on <strong>{fmtDate(accessEndDate)}</strong>
              {daysLeft !== null && (
                <span style={{ color: accent, fontWeight: 700 }}>
                  {'  '}({daysLeft} day{daysLeft === 1 ? '' : 's'} left)
                </span>
              )}
            </>
          )}
        </div>
      )}

      {alreadyRenewed ? (
        <div style={{ fontSize: '13px', color: '#166534' }}>
          ✅ Renewed —{' '}
          <a href={renewalViewerLink} style={{ color: '#0073bb', fontWeight: 600 }}>
            {renewalRequestId}
          </a>
        </div>
      ) : showRebaselineCta ? (
        <div>
          <div style={{ fontSize: '13px', color: accent, marginBottom: '10px' }}>
            This access is approaching its end date. Re-baseline to keep it active for the next cycle.
          </div>
          <a
            href={rebaselineLink}
            style={{
              display: 'inline-block',
              padding: '9px 18px',
              borderRadius: '8px',
              background: '#0073bb',
              color: '#fff',
              fontSize: '13px',
              fontWeight: '700',
              textDecoration: 'none',
            }}
          >
            🔄 Re-baseline now
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default ExpiryRenewal;
